function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { MAX_STORE_SIZE } from "../config/createGraphQlDataClient";
import { getFieldNodesWithSpreadFragmentFields } from "../graphql/documentUtils/entrypoint";
import ObservableFragment from "./ObservableFragment";
import ObservableQuery from "./ObservableQuery";
import { generateHashForDocument } from "../queryUtils/documentHashing";
import { buildQueryAndVariablesHash } from "../queryUtils/queryAndVariablesHashing";
import { reportRuntimeError } from "../telemetry/internalReporting";
function evictOldest(map) {
    const oldest = map.keys().next().value;
    if (oldest) {
        map.delete(oldest);
    }
}
class ObservableStore {
    getObservableFragment({ document, variables }) {
        const { documentHashWithVariables } = buildQueryAndVariablesHash(document, variables);
        return this.observableFragmentMap.get(documentHashWithVariables);
    }
    getObservableQuery({ document, variables }) {
        const { documentHashWithVariables } = buildQueryAndVariablesHash(document, variables);
        return this.observableQueryMap.get(documentHashWithVariables);
    }
    createObservableFragment({ document, variables }) {
        const observable = this.getObservableFragment({
            document,
            variables
        });
        if (observable) {
            return observable;
        }
        const observableFragment = new ObservableFragment(document, variables, this.resultStore);
        const { documentHash, documentHashWithVariables } = buildQueryAndVariablesHash(document, variables);
        const documentTopLevelFields = getFieldNodesWithSpreadFragmentFields(document);
        this.addObservableFragment(documentHashWithVariables, observableFragment);
        this.registerObservableDataOnTopLevelFields(documentTopLevelFields, documentHash);
        return observableFragment;
    }
    createObservableQuery({ document, variables }) {
        const observable = this.getObservableQuery({
            document,
            variables
        });
        if (observable) {
            return observable;
        }
        const { documentHash, documentHashWithVariables } = buildQueryAndVariablesHash(document, variables);
        const observableQuery = new ObservableQuery(document, variables, this.resultStore, this.memoryCache, this.apolloLinkExecutor, this.observabilityFns);
        const documentTopLevelFields = getFieldNodesWithSpreadFragmentFields(document);
        this.addObservableQuery(documentHashWithVariables, observableQuery);
        this.registerObservableDataOnTopLevelFields(documentTopLevelFields, documentHash);
        return observableQuery;
    }
    addRootQueryRefetchData(rootQuery, refetchFragments, includedFragmentHashes) {
        const rootQueryHash = generateHashForDocument(rootQuery);
        this.rootQueryRefetchDataMap.set(rootQueryHash, {
            refetchFragments,
            includedFragmentHashes
        });
    }
    removeRootQueryRefetchData(rootQuery) {
        const rootQueryHash = generateHashForDocument(rootQuery);
        this.rootQueryRefetchDataMap.delete(rootQueryHash);
    }
    invalidateTopLevelFields(topLevelFields) {
        topLevelFields.forEach((field)=>this.pendingTopLevelFields.add(field));
        if (!this.isBatchScheduled) {
            this.isBatchScheduled = true;
            setTimeout(()=>{
                this.processPendingInvalidations();
            }, 0);
        }
    }
    processPendingInvalidations() {
        if (this.pendingTopLevelFields.size === 0) {
            this.isBatchScheduled = false;
            return;
        }
        const topLevelFields = Array.from(this.pendingTopLevelFields);
        this.pendingTopLevelFields.clear();
        this.isBatchScheduled = false;
        const queryHashes = this.getObservableHashesForTopLevelFields(topLevelFields);
        this.resultStore.invalidateQueriesByDocumentHash(queryHashes);
        const { observableFragmentHashes, observableQueries } = this.zipDocumentHashesPerObservableType(queryHashes);
        const observableFragmentHashesWithVariables = observableFragmentHashes.map((hash)=>({
                hash,
                variables: null
            }));
        this.triggerFragmentsRefetch(observableFragmentHashesWithVariables);
        triggerQueriesRefetch(observableQueries);
    }
    refetchSingleFragment({ document, variables }) {
        // undefined variables get treated as empty object down the line.
        const finalVariables = variables || {};
        const { documentHash, documentHashWithVariables } = buildQueryAndVariablesHash(document, finalVariables);
        const fragment = this.observableFragmentMap.get(documentHashWithVariables);
        if (!fragment) {
            reportRuntimeError(this.observabilityFns, new Error('Fragment not found'), 'Could not find a fragment in observable store');
            return;
        }
        fragment.clearLastFetchedBy();
        this.pendingFragmentRefetches.set(documentHashWithVariables, {
            hash: documentHash,
            variables: finalVariables
        });
        if (!this.isFragmentBatchScheduled) {
            this.isFragmentBatchScheduled = true;
            setTimeout(()=>{
                this.processPendingFragmentRefetches();
            }, 0);
        }
    }
    processPendingFragmentRefetches() {
        if (this.pendingFragmentRefetches.size === 0) {
            this.isFragmentBatchScheduled = false;
            return;
        }
        const fragmentsToRefetch = Array.from(this.pendingFragmentRefetches.values());
        this.pendingFragmentRefetches.clear();
        this.isFragmentBatchScheduled = false;
        this.triggerFragmentsRefetch(fragmentsToRefetch);
    }
    zipDocumentHashesPerObservableType(hashes) {
        const observableFragmentHashes = hashes.filter((hash)=>{
            const activeEntryIncludingHash = Array.from(this.observableFragmentMap.entries()).find(([key, fragment])=>{
                return key.includes(hash) && fragment.isActive();
            });
            return typeof activeEntryIncludingHash !== 'undefined';
        });
        const observableQueries = Array.from(this.observableQueryMap.entries()).filter(([key, query])=>{
            return hashes.some((hash)=>key.includes(hash)) && query.isActive();
        }).map(([_key, query])=>{
            return query;
        });
        return {
            observableFragmentHashes,
            observableQueries
        };
    }
    triggerFragmentsRefetch(fragments) {
        const rootQueryRefetchCallbacks = Array.from(this.rootQueryRefetchDataMap.values());
        const rootQueriesToRefetch = rootQueryRefetchCallbacks.filter((rootQueryData)=>rootQueryData.includedFragmentHashes.find((fragmentHash)=>fragments.some((fragment)=>fragment.hash === fragmentHash)));
        for (const { refetchFragments } of rootQueriesToRefetch){
            refetchFragments(fragments);
        }
    }
    addObservableQuery(documentHashWithVariables, observableData) {
        this.observableQueryMap.set(documentHashWithVariables, observableData);
        if (this.observableQueryMap.size > MAX_STORE_SIZE) {
            evictOldest(this.observableQueryMap);
        }
    }
    addObservableFragment(documentHashWithVariables, observableData) {
        this.observableFragmentMap.set(documentHashWithVariables, observableData);
        if (this.observableFragmentMap.size > MAX_STORE_SIZE) {
            evictOldest(this.observableFragmentMap);
        }
    }
    registerObservableDataOnTopLevelFields(topLevelFieldNodes, documentHash) {
        const topLevelFieldNames = topLevelFieldNodes.map((field)=>field.name.value);
        topLevelFieldNames.forEach((fieldName)=>{
            const existingSet = this.topLevelFieldsToObservableHashesMap.get(fieldName);
            if (typeof existingSet === 'undefined') {
                this.topLevelFieldsToObservableHashesMap.set(fieldName, new Set([
                    documentHash
                ]));
            } else {
                existingSet.add(documentHash);
            }
        });
        if (this.topLevelFieldsToObservableHashesMap.size > MAX_STORE_SIZE) {
            evictOldest(this.topLevelFieldsToObservableHashesMap);
        }
    }
    getObservableHashesForTopLevelFields(topLevelFields) {
        const nonDedupedTopLevelFields = topLevelFields.flatMap((field)=>{
            const existingSet = this.topLevelFieldsToObservableHashesMap.get(field);
            if (typeof existingSet === 'undefined') {
                return [];
            }
            return Array.from(existingSet);
        });
        return Array.from(new Set(nonDedupedTopLevelFields));
    }
    constructor(observabilityFns, apolloLinkExecutor, resultStore, memoryCache, pollingClock){
        _define_property(this, "observableQueryMap", void 0);
        _define_property(this, "observableFragmentMap", void 0);
        _define_property(this, "rootQueryRefetchDataMap", void 0);
        _define_property(this, "topLevelFieldsToObservableHashesMap", void 0);
        _define_property(this, "apolloLinkExecutor", void 0);
        _define_property(this, "resultStore", void 0);
        _define_property(this, "observabilityFns", void 0);
        _define_property(this, "memoryCache", void 0);
        _define_property(this, "pollingClock", void 0);
        // Batching state for invalidateQueries
        _define_property(this, "pendingTopLevelFields", new Set());
        _define_property(this, "isBatchScheduled", false);
        // Batching state for refetchSingleFragment
        _define_property(this, "pendingFragmentRefetches", new Map());
        _define_property(this, "isFragmentBatchScheduled", false);
        this.observabilityFns = observabilityFns;
        this.apolloLinkExecutor = apolloLinkExecutor;
        this.resultStore = resultStore;
        this.memoryCache = memoryCache;
        this.pollingClock = pollingClock;
        this.observableQueryMap = new Map();
        this.observableFragmentMap = new Map();
        this.rootQueryRefetchDataMap = new Map();
        this.topLevelFieldsToObservableHashesMap = new Map();
    }
}
export { ObservableStore as default };
function triggerQueriesRefetch(observableQueries) {
    observableQueries.forEach((observableQuery)=>{
        observableQuery.refetch();
    });
}

//# sourceMappingURL=ObservableStore.js.map